import { React, useMemo, useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import TransactionSelectors from 'Stores/Transaction/Selectors'
import TransactionActions from 'Stores/Transaction/Actions'
import { IFText, IFTable, IFButton } from 'Components'
import Styles from './TransactionsPageList.module.css'
import { useTranslation } from 'react-i18next'
import RequestState from 'Enums/RequestState'
import Colors from 'Theme/Colors'
import { formatDate } from '../../Utils/StringFunctions'
import { hasBalance, formatBalanceAmount } from '../../Utils/BalanceFunctions'
import { Skeleton } from '@mui/material'
import ReportIcon from '@mui/icons-material/Report'
const currencyCodes = require('iso4217')

const TransactionsPageList = ({
  transactions,
  transactionsFilter,
  paginationOffset,
  fetchTransactionsListRequestState,
  fetchTransactionsList,
  tablesHeight,
}) => {
  const { t } = useTranslation()
  const loadMoreData = () => {
    if (
      fetchTransactionsListRequestState === RequestState.LOADING ||
      paginationOffset === null
    )
      return
    fetchTransactionsList(transactionsFilter, paginationOffset, failedRequest)
  }
  const [failedRequest, setFailedRequest] = useState(false)
  useEffect(() => {
    if (
      fetchTransactionsListRequestState === RequestState.ERROR_0_NETWORK ||
      fetchTransactionsListRequestState === RequestState.ERROR_400_OCCURRED ||
      fetchTransactionsListRequestState === RequestState.ERROR_401_OCCURRED ||
      fetchTransactionsListRequestState === RequestState.ERROR_403_OCCURRED ||
      fetchTransactionsListRequestState === RequestState.ERROR_409_OCCURRED ||
      fetchTransactionsListRequestState === RequestState.ERROR_UNKNOWN_OCCURRED
    )
      setFailedRequest(true)
    if (fetchTransactionsListRequestState === RequestState.SUCCEEDED)
      setFailedRequest(false)
  }, [fetchTransactionsListRequestState])
  const handleRetry = () => {
    fetchTransactionsList(transactionsFilter, paginationOffset)
  }
  const transactionTableColumns = useMemo(
    () => [
      {
        accessorKey: 'transactionId',
        size: 192,
        header:
          fetchTransactionsListRequestState === RequestState.LOADING &&
          paginationOffset === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('TransactionsPage.TransactionTableColumns.TransactionId')
          ),
        Cell: ({ row }) => {
          return row.index < transactions.length ? (
            <div className={Styles.ColumnTitle}>
              <IFText> {row.original._id}</IFText>
            </div>
          ) : failedRequest ? (
            <div className={Styles.IconDiv}>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'transactionType',
        size: 68,
        header:
          fetchTransactionsListRequestState === RequestState.LOADING &&
          paginationOffset === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('TransactionsPage.TransactionTableColumns.TransactionType')
          ),
        Cell: ({ row }) => {
          return row.index < transactions.length ? (
            <div className={Styles.ColumnTitle}>
              <IFText> {row.original.type}</IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'owner.id',
        size: 192,
        header:
          fetchTransactionsListRequestState === RequestState.LOADING &&
          paginationOffset === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('TransactionsPage.TransactionTableColumns.UserId')
          ),
        Cell: ({ row }) => {
          return row.index < transactions.length ? (
            <div className={Styles.ColumnTitle}>
              <IFText>{row.original.owner?.id}</IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'transactionAmount',
        size: 92,
        header:
          fetchTransactionsListRequestState === RequestState.LOADING &&
          paginationOffset === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('TransactionsPage.TransactionTableColumns.Amount')
          ),
        Cell: ({ row }) => {
          return row.index < transactions.length ? (
            <div className={Styles.ColumnTitle}>
              <IFText>
                {row.original.isCredit
                  ? `+${row.original.country?.currency?.code} ${Number(
                      row.original.amount,
                    ).toFixed(
                      row.original.country?.currency?.decimalPlaces || 2,
                    )}`
                  : `-${row.original.country?.currency?.code} ${Number(
                      row.original.amount,
                    ).toFixed(
                      row.original.country?.currency?.decimalPlaces || 2,
                    )}`}
              </IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'balance',
        size: 200,
        header:
          fetchTransactionsListRequestState === RequestState.LOADING &&
          paginationOffset === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('TransactionsPage.TransactionTableColumns.Balance')
          ),
        Cell: ({ row }) => {
          if (!(row.index < transactions.length))
            return failedRequest ? (
              <></>
            ) : (
              <Skeleton
                variant="text"
                width="60%"
                animation="none"
                height={20}
              />
            )

          // Absent on transactions predating the field, and on any the backfill
          // could not attribute to a billing account.
          if (!hasBalance(row.original))
            return (
              <div className={Styles.BalanceCell}>
                <IFText style={{ color: Colors.DetailPanel }}>
                  {t(
                    'TransactionsPage.TransactionTableColumns.BalanceNotRecorded',
                  )}
                </IFText>
              </div>
            )

          const currency = row.original.country?.currency
          const moved =
            row.original.billingAccountBalance.after -
            row.original.billingAccountBalance.before
          return (
            <div className={Styles.BalanceCell}>
              <IFText className={Styles.BalanceValue}>
                {formatBalanceAmount(
                  row.original.billingAccountBalance.before,
                  currency,
                )}
              </IFText>
              <IFText
                style={{
                  color:
                    moved === 0
                      ? Colors.DetailPanel
                      : moved > 0
                        ? Colors.green
                        : Colors.red,
                }}
              >
                →
              </IFText>
              <IFText className={Styles.BalanceValue}>
                {formatBalanceAmount(
                  row.original.billingAccountBalance.after,
                  currency,
                )}
              </IFText>
            </div>
          )
        },
      },
      {
        accessorKey: 'stationId',
        size: 160,
        header:
          fetchTransactionsListRequestState === RequestState.LOADING &&
          paginationOffset === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('TransactionsPage.TransactionTableColumns.StationID')
          ),
        Cell: ({ row }) => {
          return row.index < transactions.length ? (
            <div className={Styles.ColumnTitle} style={{ textAlign: 'center' }}>
              <IFText>
                {row.original.details?.remark
                  ? row.original.details?.remark
                  : '-'}
              </IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'connectorUid',
        size: 100,
        header:
          fetchTransactionsListRequestState === RequestState.LOADING &&
          paginationOffset === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('TransactionsPage.TransactionTableColumns.ConnectorUid')
          ),
        Cell: ({ row }) => {
          return row.index < transactions.length ? (
            <div className={Styles.ColumnTitle}>
              <IFText>
                {row.original.connectorUid ? row.original.connectorUid : '-'}
              </IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },

      {
        accessorKey: 'transactionEnergyConsumption',
        size: 80,
        header:
          fetchTransactionsListRequestState === RequestState.LOADING &&
          paginationOffset === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('TransactionsPage.TransactionTableColumns.EnergyConsumption')
          ),
        Cell: ({ row }) => {
          const energyConsumption = row.original?.energyConsumption
          return row.index < transactions.length ? (
            <div className={Styles.ColumnTitle}>
              <IFText>
                {energyConsumption !== null && energyConsumption !== undefined
                  ? `${Number(energyConsumption).toFixed(2)} kWh`
                  : '-'}
              </IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'transactionDate',
        header:
          fetchTransactionsListRequestState === RequestState.LOADING &&
          paginationOffset === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('TransactionsPage.TransactionTableColumns.TransactionDate')
          ),
        Cell: ({ row }) => {
          return row.index < transactions.length ? (
            <div className={Styles.ColumnTitle}>
              <IFText> {formatDate(row.original.createdAt)}</IFText>
            </div>
          ) : failedRequest ? (
            <div className={Styles.IconDiv}>
              <IFButton
                size="sm"
                color={Colors.red}
                text={t('IFTable.TryAgain')}
                onClick={() => {
                  handleRetry()
                }}
              />
            </div>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
    ],

    [
      transactions,
      fetchTransactionsListRequestState,
      failedRequest,
      transactionsFilter,
    ],
  )
  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )

  return (
    <div className={Styles.tables}>
      {((fetchTransactionsListRequestState !== RequestState.SUCCEEDED &&
        !paginationOffset) ||
        transactions) && (
        <IFTable
          columns={transactionTableColumns}
          data={transactions}
          fetchMoreOnBottomReached={fetchMoreOnBottomReached}
          paginationOffset={paginationOffset}
          filters={transactionsFilter}
          showSkeleton={
            (fetchTransactionsListRequestState === RequestState.LOADING &&
              paginationOffset === 0) ||
            (fetchTransactionsListRequestState !== RequestState.SUCCEEDED &&
              !paginationOffset)
          }
          tableMaxHeight={tablesHeight}
          tableSetHeight={'100%'}
          tableRequestState={fetchTransactionsListRequestState}
        />
      )}
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchTransactionsList: (filter, offset, shouldShowError) =>
      dispatch(
        TransactionActions.fetchTransactionsList(
          filter,
          offset,
          shouldShowError,
        ),
      ),
  }
}

const mapStateToProps = (state) => ({
  transactions: TransactionSelectors.getTransactionsList(state),
  paginationOffset: TransactionSelectors.getPaginationOffset(state),
  fetchTransactionsListRequestState:
    TransactionSelectors.getFetchTransactionsListRequestState(state),
  transactionsFilter: TransactionSelectors.getTransactionsFilter(state),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TransactionsPageList)
